import { AuthService } from 'src/auth/services/auth.service';
import { CertPolicies } from 'src/auth/cert-policies';
import { Component, DestroyRef, EventEmitter, OnInit, inject, signal, computed } from '@angular/core';
import { NgForm } from '@angular/forms';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { Subject, debounceTime, takeUntil } from 'rxjs';
import { LeaCodeService } from 'src/auth/services/lea-code.service';
import {
  AddWorkforceStaffCommand,
  CodeTablesClient,
  WorkforceEmploymentOptionsDto,
  LeaWorkforceManagementClient,
  WorkforcePersonMatchDto,
} from 'src/shared/services/api.service';
import { catchValidationErrors } from 'src/shared/utilities/validator-utilities';
import { WorkforceSiteDataState, withSiteAvailability } from '../workforce-site-data-state.service';

// Emitted by NgbActiveModal.close() when at least one staff member was added during this modal session, or, in
// vacancy mode, when the user leaves a vacancy that is no longer fillable so the caller can refresh it.
export interface WorkforceAddStaffModalResult {
  added: boolean;
  vacancyUnavailable?: boolean;
  reviewPersonId?: number;
  firstName?: string;
  lastName?: string;
}

@Component({
  selector: 'cert-workforce-add-staff-modal',
  templateUrl: './workforce-add-staff-modal.component.html',
  standalone: false,
})
export class WorkforceAddStaffModalComponent implements OnInit {
  readonly leaLocationId = 0;
  readonly leaName = signal('');
  readonly leaLevelDescription = computed(() => (this.leaName() ? `${this.leaName()} · LEA level` : 'LEA level'));
  leaCodeId!: number;
  // When set, the lookup opens with this EIN already searched.
  initialEin?: string;
  // When set with initialEin, the lookup is skipped for this person unless they are already active in the LEA.
  initialPersonId?: number;
  // When set, the modal fills this vacancy: site and position are fixed and success returns straight to review.
  vacancyStaffPositionId?: number;
  temporaryCoverage = false;
  readonly vacancyUnavailable = signal(false);
  readonly vacancyUnavailableOnSave = signal(false);
  readonly step = signal<'lookup' | 'employment' | 'confirm' | 'added' | 'person'>('lookup');
  readonly options = signal<WorkforceEmploymentOptionsDto | null>(null);
  readonly isLoadingOptions = signal(false);
  readonly isSaving = signal(false);
  readonly canCreatePerson = inject(AuthService).hasPolicy(CertPolicies.CanLeaCreatePersons);
  readonly createdPerson = signal<WorkforcePersonMatchDto | null>(null);
  readonly hasAddedStaff = signal(false);
  private scopeChanged = false;
  readonly draft = signal<Partial<AddWorkforceStaffCommand>>({});
  private readonly siteDataState = inject(WorkforceSiteDataState, { optional: true });
  readonly siteOptions = computed(() => {
    const sites = withSiteAvailability(this.options()?.sites ?? [], (site) => site.id, this.siteDataState);
    return this.requiredSiteId() !== null
      ? sites
      : [
          { id: this.leaLocationId, description: this.leaLevelDescription(), disabled: false, blockedReason: null },
          ...sites,
        ];
  });
  readonly positions = computed(() =>
    (this.options()?.positions ?? [])
      .filter((p) => (p.leaSiteCodeId ?? undefined) === this.draft().leaSiteCodeId)
      // Plain-text form of the option so ng-select can search by identifier and title.
      .map((p) => ({ ...p, label: [p.positionLocalIdentifier, p.description].filter((part) => !!part).join(' ') })),
  );
  readonly position = computed(() => this.positions().find((p) => p.id === this.draft().staffPositionId));
  // Site users can only add staff to their own site, so the site is fixed and LEA-only employment is unavailable.
  readonly requiredSiteId = computed(() => this.options()?.requiredLeaSiteCodeId ?? null);
  readonly siteName = computed(
    () =>
      this.options()?.sites.find((s) => s.id === this.draft().leaSiteCodeId)?.description ?? this.leaLevelDescription(),
  );
  readonly headerBreadcrumb = computed(() => {
    switch (this.step()) {
      case 'person':
        return 'Create person';
      case 'lookup':
        return 'Find person';
      case 'confirm':
        return 'Confirm employment';
      case 'added':
        return 'Staff added';
      default:
        return 'Employment details';
    }
  });
  readonly employmentSubmitLabel = computed(() => {
    if (this.isSaving()) return 'Saving';
    return this.step() === 'confirm' ? 'Add to Roster' : 'Review Employment';
  });
  startDate: Date | undefined;
  assignmentStartDate: Date | undefined;
  readonly activeModal = inject(NgbActiveModal);
  readonly lookupFields = signal({ firstName: '', lastName: '', ssn: '', edShId: '' });
  readonly hasMoreMatches = signal(false);
  readonly ein = signal('');
  readonly matches = signal<WorkforcePersonMatchDto[]>([]);
  readonly selectedPerson = signal<WorkforcePersonMatchDto | null>(null);
  readonly searchedEin = signal<string | null>(null);
  readonly isSearching = signal(false);
  readonly error = signal<string | null>(null);
  readonly errorEmitter = new EventEmitter<{ modelErrors: string[][]; allErrors: string[][] } | null>();
  private readonly client = inject(LeaWorkforceManagementClient);
  private readonly codeTablesClient = inject(CodeTablesClient);
  private readonly leaCodeService = inject(LeaCodeService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly cancelSearch$ = new Subject<void>();
  private readonly searchChanges$ = new Subject<NgForm | null>();

  ngOnInit() {
    this.codeTablesClient
      .getLeaCodes()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (leas) => this.leaName.set(leas.find((lea) => lea.leaCodeId === this.leaCodeId)?.description ?? ''),
        error: () => this.leaName.set(''),
      });

    this.searchChanges$.pipe(debounceTime(400), takeUntilDestroyed(this.destroyRef)).subscribe((form) => {
      if (form && this.hasSearchCriteria()) this.search(form);
    });

    this.leaCodeService
      .getCurrentLeaCodeId()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((leaCodeId) => {
        if (leaCodeId !== this.leaCodeId) {
          this.scopeChanged = true;
          this.cancel();
        }
      });

    if (!this.scopeChanged && this.initialEin?.trim()) {
      this.ein.set(this.initialEin.trim());
      this.search();
    }
  }

  changeEin(value: string, form?: NgForm) {
    this.ein.set(value);
    this.resetSearch(form);
  }

  changeLookup(field: 'firstName' | 'lastName' | 'ssn' | 'edShId', value: string, form?: NgForm) {
    this.lookupFields.update((fields) => ({ ...fields, [field]: value }));
    this.resetSearch(form);
  }

  hasSearchCriteria() {
    const fields = this.lookupFields();
    return (
      !!this.ein().trim() ||
      !!fields.edShId.trim() ||
      fields.firstName.replace(/[^a-z0-9]/gi, '').length >= 2 ||
      fields.lastName.replace(/[^a-z0-9]/gi, '').length >= 3 ||
      fields.ssn.trim().length >= 4
    );
  }

  resetSearch(form?: NgForm) {
    this.cancelSearch$.next();
    this.hasMoreMatches.set(false);
    this.matches.set([]);
    this.selectedPerson.set(null);
    this.searchedEin.set(null);
    this.isSearching.set(false);
    this.error.set(null);
    this.errorEmitter.emit(null);
    this.searchChanges$.next(form ?? null);
  }

  search(form?: NgForm) {
    this.changeEin(this.ein());
    const ein = this.ein().trim();
    this.isSearching.set(true);
    this.client
      .searchPerson({
        leaCodeId: this.leaCodeId,
        ein,
        ...(this.lookupFields().firstName.trim() ? { firstName: this.lookupFields().firstName.trim() } : {}),
        ...(this.lookupFields().lastName.trim() ? { lastName: this.lookupFields().lastName.trim() } : {}),
        ...(this.lookupFields().ssn.trim() ? { ssn: this.lookupFields().ssn.trim() } : {}),
        ...(this.lookupFields().edShId.trim() ? { edShId: this.lookupFields().edShId.trim() } : {}),
      })
      .pipe(
        // Cancel before validation mapping too, so a stale 422 cannot alter a newer form.
        takeUntil(this.cancelSearch$),
        takeUntilDestroyed(this.destroyRef),
        catchValidationErrors(form, this.errorEmitter),
      )
      .subscribe({
        next: (result) => {
          this.isSearching.set(false);
          this.matches.set(result.matches);
          this.hasMoreMatches.set(result.hasMoreMatches);
          this.searchedEin.set(ein);
          const initialPerson = result.matches.find((match) => match.personId === this.initialPersonId);
          this.initialPersonId = undefined;
          if (initialPerson && !initialPerson.isAlreadyActiveInLea) {
            this.selectPerson(initialPerson);
            this.loadEmployment();
          }
        },
        error: (error: { status?: number }) => {
          this.isSearching.set(false);
          if (error.status === 422) return;
          this.error.set(
            error.status === 403 || error.status === 404
              ? 'Staff lookup is unavailable for your access to the selected LEA.'
              : 'Unable to search the state directory. Please try again.',
          );
        },
      });
  }

  beginCreatePerson() {
    if (!this.canCreatePerson || this.isSearching() || this.searchedEin() === null) return;
    this.step.set('person');
  }

  identityBusyChanged(busy: boolean) {
    this.isSaving.set(busy);
    if (!busy && this.scopeChanged) this.cancel();
  }

  acceptIdentity(result: { person: WorkforcePersonMatchDto; created: boolean }) {
    this.isSaving.set(false);
    this.lookupFields.set({ firstName: '', lastName: '', ssn: '', edShId: '' });
    if (result.created) this.createdPerson.set(result.person);
    this.ein.set(result.person.ein ?? '');
    this.matches.set([result.person]);
    this.selectedPerson.set(result.person);
    if (this.scopeChanged) {
      this.cancel();
      return;
    }
    this.loadEmployment();
  }

  loadEmployment() {
    if (!this.selectedPerson() || this.isLoadingOptions()) return;
    this.lookupFields.set({ firstName: '', lastName: '', ssn: '', edShId: '' });
    this.step.set('employment');
    this.isLoadingOptions.set(true);
    this.error.set(null);
    this.client
      .getEmploymentOptions(this.leaCodeId)
      .pipe(takeUntil(this.cancelSearch$), takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (options) => {
          this.options.set(options);
          this.isLoadingOptions.set(false);
          if (this.vacancyStaffPositionId !== undefined) this.applyVacancy(options);
          else if (options.requiredLeaSiteCodeId != null)
            this.draft.update((draft) => ({ ...draft, leaSiteCodeId: options.requiredLeaSiteCodeId }));
        },
        error: () => {
          this.isLoadingOptions.set(false);
          this.error.set('Unable to load employment choices. Please try again.');
        },
      });
  }

  applyVacancy(options: WorkforceEmploymentOptionsDto) {
    const vacancy = options.positions.find((p) => p.id === this.vacancyStaffPositionId);
    this.vacancyUnavailable.set(!vacancy);
    if (!vacancy) return;
    this.draft.update((draft) => ({
      ...draft,
      leaSiteCodeId: vacancy.leaSiteCodeId ?? undefined,
      staffPositionId: vacancy.id,
      fte: draft.fte ?? vacancy.fte ?? undefined,
    }));
  }

  updateDraft<K extends keyof AddWorkforceStaffCommand>(key: K, value: AddWorkforceStaffCommand[K] | null) {
    this.draft.update((draft) => ({ ...draft, [key]: value ?? undefined }));
    if (key === 'leaSiteCodeId') this.updateDraft('staffPositionId', undefined);
    if (key === 'staffPositionId') {
      this.draft.update((draft) => ({
        ...draft,
        fte: undefined,
        periodsTaught: undefined,
        modeOfInstructionCodeId: undefined,
        teacherOfRecord: undefined,
      }));
    }
  }

  backToLookup() {
    if (this.isSaving()) return;
    this.step.set('lookup');
    this.options.set(null);
    this.vacancyUnavailable.set(false);
    this.vacancyUnavailableOnSave.set(false);
    this.draft.set({});
    this.error.set(null);
    this.errorEmitter.emit(null);
  }

  goBack() {
    if (this.step() === 'confirm') this.step.set('employment');
    else this.backToLookup();
  }

  // Resets the whole session so another person can be searched for and added without leaving the modal.
  addAnother() {
    this.hasMoreMatches.set(false);
    this.createdPerson.set(null);
    this.step.set('lookup');
    this.options.set(null);
    this.draft.set({});
    this.error.set(null);
    this.errorEmitter.emit(null);
    this.ein.set('');
    this.lookupFields.set({ firstName: '', lastName: '', ssn: '', edShId: '' });
    this.matches.set([]);
    this.selectedPerson.set(null);
    this.searchedEin.set(null);
    this.startDate = undefined;
    this.assignmentStartDate = undefined;
  }

  reviewNewStaff() {
    this.activeModal.close({ added: true, reviewPersonId: this.selectedPerson()?.personId });
  }

  returnToRoster() {
    this.activeModal.close({ added: true });
  }

  submitEmployment(form: NgForm) {
    if (this.scopeChanged || this.isSaving() || !this.options() || !this.selectedPerson()) return;
    if (this.vacancyUnavailable()) return;
    if (this.step() === 'employment') {
      this.step.set('confirm');
      return;
    }
    this.isSaving.set(true);
    this.error.set(null);
    this.errorEmitter.emit(null);
    const command: AddWorkforceStaffCommand = {
      ...this.draft(),
      leaCodeId: this.leaCodeId,
      personId: this.selectedPerson()!.personId,
      schoolYear: this.options()!.schoolYear,
      startDate: this.startDate ? new Date(this.startDate) : undefined,
      assignmentStartDate:
        this.draft().staffPositionId && this.assignmentStartDate ? new Date(this.assignmentStartDate) : undefined,
    };
    this.client
      .addStaff(command)
      .pipe(takeUntilDestroyed(this.destroyRef), catchValidationErrors(form, this.errorEmitter))
      .subscribe({
        next: () => {
          this.isSaving.set(false);
          this.hasAddedStaff.set(true);
          if (this.vacancyStaffPositionId !== undefined) {
            const person = this.selectedPerson()!;
            this.activeModal.close({
              added: true,
              reviewPersonId: person.personId,
              firstName: person.firstName,
              lastName: person.lastName,
            });
            return;
          }
          this.step.set('added');
        },
        error: (error: { status?: number }) => {
          if (this.scopeChanged) {
            this.isSaving.set(false);
            this.activeModal.dismiss();
            return;
          }
          if (error.status === 422 && this.vacancyStaffPositionId !== undefined) {
            this.recheckVacancyAfterRejection();
            return;
          }
          this.isSaving.set(false);
          this.step.set('employment');
          if (error.status !== 422)
            this.error.set('Unable to confirm the save. Your draft is retained. Check the roster before retrying.');
        },
      });
  }

  // A rejected vacancy save may mean the position was filled, closed or changed meanwhile; the options query decides.
  recheckVacancyAfterRejection() {
    this.client
      .getEmploymentOptions(this.leaCodeId)
      .pipe(takeUntil(this.cancelSearch$), takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (options) => {
          this.isSaving.set(false);
          this.step.set('employment');
          if (options.positions.some((p) => p.id === this.vacancyStaffPositionId)) return;
          this.errorEmitter.emit(null);
          this.vacancyUnavailableOnSave.set(true);
          this.vacancyUnavailable.set(true);
        },
        error: () => {
          this.isSaving.set(false);
          this.step.set('employment');
        },
      });
  }

  selectPerson(person: WorkforcePersonMatchDto) {
    if (person.isAlreadyActiveInLea || !this.matches().includes(person)) return;
    this.selectedPerson.set(person);
  }

  cancel() {
    if (this.isSaving()) return;
    this.searchChanges$.next(null);
    this.cancelSearch$.next();
    this.matches.set([]);
    this.selectedPerson.set(null);
    this.lookupFields.set({ firstName: '', lastName: '', ssn: '', edShId: '' });
    // Staff already saved this session must still refresh the roster, even when closed from an earlier step.
    if (this.hasAddedStaff()) this.activeModal.close({ added: true });
    else if (this.vacancyUnavailable()) this.activeModal.close({ added: false, vacancyUnavailable: true });
    else this.activeModal.dismiss();
  }
}
